import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Copy, Trash2, Eye, Send, ArrowLeft, Save, Monitor, Tablet, Smartphone, FileText, LayoutTemplate, Mail, Search, Undo2, Redo2, Check, LayoutGrid, Palette, SlidersHorizontal, Layers, Braces, User, Briefcase, Building2, Package, LinkIcon, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "sonner";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import gjsNewsletter from "grapesjs-preset-newsletter";
import STARTER_TEMPLATES from "./email/starterTemplates";
import StylePanels from "./email/StylePanels";

const API = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  const t = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

// ── Template List ─────────────────────────────────────────────────────────────
function TemplateList({ templates, onEdit, onCreate, onDuplicate, onDelete, tc500, tc50 }) {
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const filtered = templates.filter(t =>
    !search || t.name?.toLowerCase().includes(search.toLowerCase())
  );

  const confirmDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  };

  return (
    <div data-testid="email-modelos-page" className="space-y-4">
      {/* Header — mesmo padrão ContatosPage */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Mail className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2" data-testid="email-modelos-title">
              Modelos de E-mail
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{templates.length}</Badge>
            </h1>
            <p className="text-slate-500 mt-0.5">Crie e gerencie seus templates de e-mail</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="template-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar modelo..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Tooltip><TooltipTrigger asChild>
            <Button data-testid="create-template-btn" onClick={onCreate} size="sm" className="text-white h-9 gap-1.5 hover:opacity-90" style={{ backgroundColor: tc500 }}>
              <Plus className="w-4 h-4" /> Criar Modelo
            </Button>
          </TooltipTrigger><TooltipContent>Novo modelo</TooltipContent></Tooltip>
        </div>
      </div>

      {filtered.length === 0 && templates.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-600 font-medium">Nenhum modelo criado</p>
          <p className="text-slate-400 text-sm mt-1">Crie seu primeiro template de e-mail</p>
          <Button onClick={onCreate} className="mt-4 gap-2" variant="outline">
            <Plus className="w-4 h-4" /> Criar Modelo
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <p className="text-slate-500">Nenhum modelo encontrado para "{search}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((t) => (
            <div key={t.template_id} data-testid={`template-card-${t.template_id}`}
              onClick={() => onEdit(t)}
              className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden hover:shadow-md transition-all group cursor-pointer"
              style={{ "--tc": tc500 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = tc500}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
              {/* Preview */}
              <div className="h-40 bg-slate-50 border-b border-slate-100 overflow-hidden relative">
                {t.html ? (
                  <iframe
                    srcDoc={t.html}
                    title={t.name}
                    className="w-full h-full pointer-events-none"
                    style={{ transform: "scale(0.4)", transformOrigin: "top left", width: "250%", height: "250%" }}
                    sandbox=""
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-300 text-sm">Sem conteúdo</div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
              </div>
              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-800 truncate">{t.name}</h3>
                    {t.description && <p className="text-xs text-slate-400 mt-1 truncate">{t.description}</p>}
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full leading-none flex-shrink-0 ${t.status === "publicado" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {t.status === "publicado" ? "Publicado" : "Rascunho"}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => onEdit(t)} className="h-8 w-8" data-testid={`edit-template-${t.template_id}`}>
                      <Pencil className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                  </TooltipTrigger><TooltipContent>Editar</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => onDuplicate(t.template_id)} className="h-8 w-8">
                      <Copy className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                  </TooltipTrigger><TooltipContent>Duplicar</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(t.template_id)} className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir modelo</DialogTitle>
            <DialogDescription>Tem certeza que deseja excluir este modelo? Esta ação não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete-btn">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Starter Template Gallery ──────────────────────────────────────────────────
function StarterGallery({ onSelect, onBack, tc500, tc50 }) {
  const iconMap = {
    blank:      <FileText className="w-8 h-8 text-slate-400" strokeWidth={1.2} />,
    newsletter: <LayoutTemplate className="w-8 h-8 text-blue-500" strokeWidth={1.2} />,
    promo:      <Send className="w-8 h-8 text-amber-500" strokeWidth={1.2} />,
    launch:     <Plus className="w-8 h-8 text-violet-500" strokeWidth={1.2} />,
    welcome:    <Send className="w-8 h-8 text-emerald-500" strokeWidth={1.2} />,
    event:      <Eye className="w-8 h-8 text-pink-500" strokeWidth={1.2} />,
    feedback:   <Pencil className="w-8 h-8 text-sky-500" strokeWidth={1.2} />,
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
          <Mail className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight" data-testid="gallery-title">Escolha um modelo inicial</h1>
          <p className="text-slate-500 mt-0.5">Selecione um template pronto ou comece do zero</p>
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={onBack} className="gap-1" data-testid="gallery-back-btn">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {STARTER_TEMPLATES.map((st) => (
          <button key={st.id} onClick={() => onSelect(st)}
            data-testid={`starter-${st.id}`}
            className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all duration-200 text-left">
            {/* Preview */}
            <div className="h-48 relative overflow-hidden bg-white border-b border-slate-100">
              {st.html ? (
                <iframe
                  srcDoc={`<html><head><style>body{margin:0;overflow:hidden;}</style></head><body>${st.html}</body></html>`}
                  title={st.name}
                  className="absolute top-0 left-1/2 pointer-events-none border-0"
                  style={{ width: "600px", height: "800px", transform: "translateX(-50%) scale(0.35)", transformOrigin: "top center" }}
                  sandbox=""
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-2" style={{ backgroundColor: st.color }}>
                  {iconMap[st.icon]}
                  <span className="text-xs text-slate-400">Canvas vazio</span>
                </div>
              )}
              <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/5 transition-colors" />
            </div>
            {/* Info */}
            <div className="p-3">
              <h3 className="font-semibold text-sm text-slate-800" dangerouslySetInnerHTML={{ __html: st.name }} />
              <p className="text-xs text-slate-400 mt-0.5" dangerouslySetInnerHTML={{ __html: st.description }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Variables Panel (for the email editor sidebar) ────────────────────────────
const VARIABLE_CATEGORIES = [
  {
    id: "contato",
    label: "Contato",
    icon: User,
    variables: [
      { key: "nome", label: "Nome" },
      { key: "email", label: "E-mail" },
      { key: "telefone", label: "Telefone" },
      { key: "empresa", label: "Empresa" },
      { key: "cargo", label: "Cargo" },
      { key: "responsavel", label: "Responsável" },
    ],
  },
  {
    id: "negocio",
    label: "Negócio",
    icon: Briefcase,
    description: "Se associado ao contato",
    variables: [
      { key: "negocio_nome", label: "Nome do negócio" },
      { key: "negocio_valor", label: "Valor" },
      { key: "negocio_status", label: "Status" },
      { key: "negocio_pipeline", label: "Pipeline" },
      { key: "negocio_estagio", label: "Estágio" },
    ],
  },
  {
    id: "empresa",
    label: "Empresa",
    icon: Building2,
    description: "Se associada ao negócio",
    variables: [
      { key: "empresa_nome", label: "Nome da empresa" },
      { key: "empresa_cnpj", label: "CNPJ" },
      { key: "empresa_telefone", label: "Telefone" },
      { key: "empresa_site", label: "Website" },
    ],
  },
  {
    id: "produto",
    label: "Produto",
    icon: Package,
    description: "Se associado ao negócio",
    variables: [
      { key: "produto_nome", label: "Nome do produto" },
      { key: "produto_preco", label: "Preço" },
      { key: "produto_codigo", label: "Código" },
    ],
  },
  {
    id: "sistema",
    label: "Sistema",
    icon: LinkIcon,
    variables: [
      { key: "link_descadastro", label: "Link de descadastro", isBlock: true },
    ],
  },
];

function VariablesPanel({ editorRef, tc500, tc50 }) {
  const [expanded, setExpanded] = useState({ contato: true });
  const [customFields, setCustomFields] = useState({ contatos: [], negocios: [] });
  const lastSelectedRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = { "Content-Type": "application/json" };
        const token = localStorage.getItem("token");
        if (token) headers.Authorization = `Bearer ${token}`;
        const [cRes, nRes] = await Promise.all([
          fetch(`${API}/api/custom-fields?module=contatos`, { credentials: "include", headers }),
          fetch(`${API}/api/custom-fields?module=negocios`, { credentials: "include", headers }),
        ]);
        const contatos = cRes.ok ? (await cRes.json()).map(f => ({
          key: `custom_${f.field_name}`, label: f.field_label,
        })) : [];
        const negocios = nRes.ok ? (await nRes.json()).map(f => ({
          key: `negocio_custom_${f.field_name}`, label: f.field_label,
        })) : [];
        setCustomFields({ contatos, negocios });
      } catch { /* ignore */ }
    })();
  }, []);

  // Track the last selected component so we can insert even after panel click
  useEffect(() => {
    const editor = editorRef?.current;
    if (!editor) return;
    const onSelect = (component) => { lastSelectedRef.current = component; };
    editor.on("component:selected", onSelect);
    return () => editor.off("component:selected", onSelect);
  }, [editorRef]);

  const insertVariable = (key, label, isBlock) => {
    const editor = editorRef?.current;
    if (!editor) return;

    if (isBlock && key === "link_descadastro") {
      editor.addComponents(`
        <table width="100%" cellpadding="0" cellspacing="0" data-gjs-type="default">
          <tr><td style="text-align:center;padding:16px 0;border-top:1px solid #e2e8f0;">
            <p style="margin:0;font-size:12px;color:#94a3b8;">
              Não deseja mais receber nossos e-mails?
              <a href="{link_descadastro}" style="color:#64748b;text-decoration:underline;">Clique aqui para se descadastrar</a>
            </p>
          </td></tr>
        </table>
      `);
      return;
    }

    const varText = `{${key}}`;
    const selected = editor.getSelected() || lastSelectedRef.current;

    if (selected) {
      const el = selected.getEl();
      if (el) {
        // Append variable text to the element's actual DOM, then sync back
        el.innerHTML = el.innerHTML + varText;
        // Force GrapeJS to pick up the DOM change
        selected.components(el.innerHTML);
      }
    } else {
      // Nothing selected — add a new text block with the variable
      editor.addComponents(`<p style="padding:8px;">${varText}</p>`);
    }
  };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const allCategories = VARIABLE_CATEGORIES.map(cat => {
    if (cat.id === "contato" && customFields.contatos.length > 0) {
      return { ...cat, variables: [...cat.variables, ...customFields.contatos] };
    }
    if (cat.id === "negocio" && customFields.negocios.length > 0) {
      return { ...cat, variables: [...cat.variables, ...customFields.negocios] };
    }
    return cat;
  });

  return (
    <div className="space-y-1" data-testid="variables-panel">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Inserir variáveis</p>
      <p className="text-[10px] text-slate-400 mb-3 leading-relaxed">
        Clique para inserir no elemento selecionado. As variáveis serão substituídas pelos dados reais no envio.
      </p>
      {allCategories.map(cat => {
        const Icon = cat.icon;
        const isOpen = expanded[cat.id];
        return (
          <div key={cat.id} className="border border-slate-100 rounded-lg overflow-hidden">
            <button
              onClick={() => toggle(cat.id)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
              data-testid={`var-category-${cat.id}`}
            >
              <Icon className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs font-medium text-slate-700 flex-1 text-left">{cat.label}</span>
              {cat.description && <span className="text-[9px] text-slate-400 hidden sm:inline">{cat.description}</span>}
              {isOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
            </button>
            {isOpen && (
              <div className="px-2 pb-2 flex flex-wrap gap-1">
                {cat.variables.map(v => (
                  <button
                    key={v.key}
                    onClick={() => insertVariable(v.key, v.label, v.isBlock)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors hover:shadow-sm cursor-pointer"
                    style={{ backgroundColor: tc50, color: tc500, borderColor: `${tc500}30` }}
                    data-testid={`insert-var-${v.key}`}
                    title={v.isBlock ? "Insere um bloco de descadastro" : `Insere {${v.key}}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Template Editor with GrapeJS ──────────────────────────────────────────────
function TemplateEditor({ template, onBack, tc500, tc50 }) {
  const editorRef = useRef(null);
  const gjsRef = useRef(null);
  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [activePanel, setActivePanel] = useState("blocks");

  const templateIdRef = useRef(template?.template_id || null);

  const saveToServer = useCallback(async (body) => {
    const tid = templateIdRef.current;
    const url = tid ? `${API}/api/email-templates/${tid}` : `${API}/api/email-templates`;
    const method = tid ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(body) });
    if (res.ok && !tid) {
      const created = await res.json();
      templateIdRef.current = created.template_id;
    }
    return res.ok;
  }, []);

  const handleUndo = () => gjsRef.current?.UndoManager?.undo();
  const handleRedo = () => gjsRef.current?.UndoManager?.redo();

  useEffect(() => {
    if (!editorRef.current || gjsRef.current) return;

    const editor = grapesjs.init({
      container: editorRef.current,
      height: "100%",
      width: "auto",
      fromElement: false,
      storageManager: false,
      plugins: [gjsNewsletter],
      pluginsOpts: {
        [gjsNewsletter]: {
          modalTitleImport: "Importar HTML",
          modalTitleExport: "Exportar HTML",
        },
      },
      deviceManager: {
        devices: [
          { name: "Desktop", width: "" },
          { name: "Tablet", width: "600px", widthMedia: "768px" },
          { name: "Mobile", width: "375px", widthMedia: "480px" },
        ],
      },
      blockManager: {
        appendTo: "#gjs-blocks-panel",
      },
      styleManager: {
        appendTo: "#gjs-styles-native-hidden",
      },
      traitManager: {
        appendTo: "#gjs-traits-panel",
      },
      layerManager: {
        appendTo: "#gjs-layers-panel",
      },
      panels: { defaults: [] },
      i18n: {
        locale: "pt",
        messages: {
          pt: {
            assetManager: {
              addButton: "Adicionar imagem",
              inputPlh: "https://caminho/para/imagem.jpg",
              modalTitle: "Selecionar Imagem",
              uploadTitle: "Solte arquivos aqui ou clique para enviar",
            },
          },
        },
      },
    });

    // ─── Set default max-width for images ──────────────────────────────
    editor.on("component:add", (component) => {
      if (component.get("type") === "image") {
        const style = component.getStyle() || {};
        if (!style["max-width"]) {
          component.setStyle({ ...style, "max-width": "200px", "height": "auto" });
        }
      }
    });

    // ─── Translate default blocks ──────────────────────────────────────
    const bm = editor.BlockManager;
    const translations = {
      "sect100": "Seção 1 coluna",
      "sect50": "Seção 1/2",
      "sect30": "Seção 1/3",
      "sect37": "Seção 3/7",
      "button": "Botão",
      "divider": "Divisor",
      "text": "Texto",
      "text-sect": "Bloco de Texto",
      "image": "Imagem",
      "quote": "Citação",
      "link": "Link",
      "link-block": "Bloco Link",
      "grid-items": "Grid",
      "list-items": "Lista",
    };
    const blockIcons = {
      "sect100": "fa fa-square-o",
      "sect50": "fa fa-columns",
      "sect30": "fa fa-th",
      "sect37": "fa fa-table",
      "button": "fa fa-hand-pointer-o",
      "divider": "fa fa-minus",
      "text": "fa fa-font",
      "text-sect": "fa fa-align-left",
      "image": "fa fa-picture-o",
      "quote": "fa fa-quote-left",
      "link": "fa fa-link",
      "link-block": "fa fa-external-link",
      "grid-items": "fa fa-th-large",
      "list-items": "fa fa-list",
    };
    Object.entries(translations).forEach(([id, label]) => {
      const block = bm.get(id);
      if (block) {
        block.set("label", label);
        block.set("category", "Para Criar");
        if (blockIcons[id]) {
          block.set("attributes", { class: blockIcons[id] });
          block.set("media", "");
        }
      }
    });

    // ─── Custom pre-styled blocks ──────────────────────────────────────
    bm.add("botao-cta", {
      label: "Botão CTA",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:20px 0;">
        <a href="#" style="display:inline-block;background:#2563eb;color:#ffffff;font-family:Arial,sans-serif;font-size:15px;font-weight:700;padding:14px 40px;border-radius:8px;text-decoration:none;">Clique aqui</a>
      </td></tr></table>`,
      attributes: { class: "fa fa-hand-pointer-o" },
    });

    bm.add("botao-outline", {
      label: "Botão Contorno",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:20px 0;">
        <a href="#" style="display:inline-block;border:2px solid #2563eb;color:#2563eb;font-family:Arial,sans-serif;font-size:14px;font-weight:600;padding:12px 36px;border-radius:8px;text-decoration:none;">Saiba mais</a>
      </td></tr></table>`,
      attributes: { class: "fa fa-square-o" },
    });

    bm.add("titulo-h1", {
      label: "Título Grande",
      category: "Prontos",
      content: `<h1 style="color:#1e293b;font-family:Arial,sans-serif;font-size:28px;font-weight:700;margin:0;padding:16px 0;">Seu título aqui</h1>`,
      attributes: { class: "fa fa-header" },
    });

    bm.add("titulo-h2", {
      label: "Subtítulo",
      category: "Prontos",
      content: `<h2 style="color:#1e293b;font-family:Arial,sans-serif;font-size:20px;font-weight:600;margin:0;padding:12px 0;">Seu subtítulo aqui</h2>`,
      attributes: { class: "fa fa-font" },
    });

    bm.add("paragrafo", {
      label: "Parágrafo",
      category: "Prontos",
      content: `<p style="color:#475569;font-family:Arial,sans-serif;font-size:14px;line-height:1.7;margin:0;padding:8px 0;">Escreva aqui o texto do seu e-mail. Substitua este conteúdo pelo que desejar enviar aos seus contatos.</p>`,
      attributes: { class: "fa fa-paragraph" },
    });

    bm.add("imagem-com-legenda", {
      label: "Imagem + Legenda",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:16px 0;">
        <img src="https://placehold.co/540x280/e2e8f0/94a3b8?text=Sua+Imagem" alt="Imagem" style="max-width:100%;height:auto;border-radius:8px;display:block;margin:0 auto;" width="540" />
        <p style="color:#94a3b8;font-family:Arial,sans-serif;font-size:12px;margin:8px 0 0;text-align:center;">Legenda da imagem</p>
      </td></tr></table>`,
      attributes: { class: "fa fa-picture-o" },
    });

    bm.add("divisor-linha", {
      label: "Divisor Linha",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:16px 0;">
        <div style="width:100%;height:1px;background:#e2e8f0;"></div>
      </td></tr></table>`,
      attributes: { class: "fa fa-minus" },
    });

    bm.add("espacador", {
      label: "Espaçador",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:32px;"></td></tr></table>`,
      attributes: { class: "fa fa-arrows-v" },
    });

    bm.add("card-destaque", {
      label: "Card Destaque",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:8px;">
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:24px;">
          <h3 style="color:#1e293b;font-family:Arial,sans-serif;font-size:16px;font-weight:600;margin:0 0 8px;">Título do card</h3>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.6;margin:0 0 16px;">Descrição breve do card com informações relevantes para o leitor.</p>
          <a href="#" style="color:#2563eb;font-family:Arial,sans-serif;font-size:13px;font-weight:600;text-decoration:none;">Ler mais →</a>
        </div>
      </td></tr></table>`,
      attributes: { class: "fa fa-clone" },
    });

    bm.add("destaque-numero", {
      label: "Métrica / Número",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr>
        <td width="33%" style="text-align:center;padding:16px;">
          <p style="color:#2563eb;font-family:Arial,sans-serif;font-size:32px;font-weight:800;margin:0;">100+</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;margin:4px 0 0;">Clientes</p>
        </td>
        <td width="33%" style="text-align:center;padding:16px;">
          <p style="color:#2563eb;font-family:Arial,sans-serif;font-size:32px;font-weight:800;margin:0;">4.9</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;margin:4px 0 0;">Avaliação</p>
        </td>
        <td width="33%" style="text-align:center;padding:16px;">
          <p style="color:#2563eb;font-family:Arial,sans-serif;font-size:32px;font-weight:800;margin:0;">24h</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;margin:4px 0 0;">Suporte</p>
        </td>
      </tr></table>`,
      attributes: { class: "fa fa-bar-chart" },
    });

    bm.add("cupom", {
      label: "Cupom / Código",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:20px 0;">
        <div style="background:#fffbeb;border:2px dashed #f59e0b;border-radius:10px;padding:16px 32px;display:inline-block;">
          <p style="color:#92400e;font-family:Arial,sans-serif;font-size:12px;margin:0 0 4px;text-transform:uppercase;letter-spacing:1px;">Seu cupom</p>
          <p style="color:#92400e;font-family:Arial,sans-serif;font-size:22px;font-weight:800;letter-spacing:4px;margin:0;">DESCONTO20</p>
        </div>
      </td></tr></table>`,
      attributes: { class: "fa fa-ticket" },
    });

    bm.add("redes-sociais", {
      label: "Redes Sociais",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:20px 0;">
        <a href="#" style="display:inline-block;width:36px;height:36px;background:#1877F2;border-radius:50%;margin:0 6px;text-align:center;line-height:36px;text-decoration:none;color:#fff;font-family:Arial;font-size:16px;font-weight:700;">f</a>
        <a href="#" style="display:inline-block;width:36px;height:36px;background:#E4405F;border-radius:50%;margin:0 6px;text-align:center;line-height:36px;text-decoration:none;color:#fff;font-family:Arial;font-size:14px;font-weight:700;">in</a>
        <a href="#" style="display:inline-block;width:36px;height:36px;background:#0A66C2;border-radius:50%;margin:0 6px;text-align:center;line-height:36px;text-decoration:none;color:#fff;font-family:Arial;font-size:14px;font-weight:700;">in</a>
        <a href="#" style="display:inline-block;width:36px;height:36px;background:#1DA1F2;border-radius:50%;margin:0 6px;text-align:center;line-height:36px;text-decoration:none;color:#fff;font-family:Arial;font-size:14px;font-weight:700;">X</a>
      </td></tr></table>`,
      attributes: { class: "fa fa-share-alt" },
    });

    bm.add("rodape-email", {
      label: "Rodapé",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="background:#f8fafc;padding:24px 40px;text-align:center;border-top:1px solid #e2e8f0;">
        <p style="color:#94a3b8;font-family:Arial,sans-serif;font-size:12px;margin:0 0 4px;">Sua Empresa &bull; Endereço completo</p>
        <p style="color:#94a3b8;font-family:Arial,sans-serif;font-size:11px;margin:0;">Você recebeu este e-mail porque se cadastrou em nossa lista. <a href="#" style="color:#2563eb;text-decoration:underline;">Cancelar inscrição</a></p>
      </td></tr></table>`,
      attributes: { class: "fa fa-ellipsis-h" },
    });

    bm.add("header-colorido", {
      label: "Header Colorido",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="background:#2563eb;padding:32px 40px;text-align:center;">
        <h1 style="color:#ffffff;font-family:Arial,sans-serif;font-size:24px;font-weight:700;margin:0;">Título do E-mail</h1>
        <p style="color:#93c5fd;font-family:Arial,sans-serif;font-size:13px;margin:8px 0 0;">Subtítulo ou descrição breve</p>
      </td></tr></table>`,
      attributes: { class: "fa fa-bookmark" },
    });

    bm.add("depoimento-bloco", {
      label: "Depoimento",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:8px;">
        <div style="background:#f0fdf4;border-radius:12px;padding:24px;border-left:4px solid #16a34a;">
          <p style="color:#475569;font-family:Arial,sans-serif;font-size:14px;line-height:1.7;font-style:italic;margin:0 0 12px;">"Depoimento do cliente sobre o produto ou serviço. Uma experiência incrível que vale a pena compartilhar."</p>
          <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:13px;font-weight:600;margin:0;">Nome do Cliente, Cargo</p>
        </div>
      </td></tr></table>`,
      attributes: { class: "fa fa-quote-left" },
    });

    bm.add("alerta-info", {
      label: "Alerta / Aviso",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:8px;">
        <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:16px 20px;">
          <p style="color:#1e40af;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 4px;">ℹ Informação importante</p>
          <p style="color:#1e40af;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;margin:0;">Adicione aqui uma mensagem de destaque ou aviso para os seus destinatários.</p>
        </div>
      </td></tr></table>`,
      attributes: { class: "fa fa-info-circle" },
    });

    bm.add("dois-botoes", {
      label: "2 Botões",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:20px 0;">
        <a href="#" style="display:inline-block;background:#2563eb;color:#ffffff;font-family:Arial,sans-serif;font-size:14px;font-weight:700;padding:12px 32px;border-radius:8px;text-decoration:none;margin:0 6px;">Aceitar</a>
        <a href="#" style="display:inline-block;background:#ffffff;color:#475569;font-family:Arial,sans-serif;font-size:14px;font-weight:600;padding:10px 32px;border-radius:8px;text-decoration:none;border:2px solid #e2e8f0;margin:0 6px;">Recusar</a>
      </td></tr></table>`,
      attributes: { class: "fa fa-columns" },
    });

    bm.add("imagem-texto", {
      label: "Imagem + Texto",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr>
        <td width="45%" style="vertical-align:top;padding:8px;">
          <img src="https://placehold.co/260x200/e2e8f0/94a3b8?text=Imagem" alt="Imagem" style="width:100%;height:auto;border-radius:8px;display:block;" />
        </td>
        <td width="55%" style="vertical-align:top;padding:8px 8px 8px 16px;">
          <h3 style="color:#1e293b;font-family:Arial,sans-serif;font-size:18px;font-weight:600;margin:0 0 8px;">Título da seção</h3>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.6;margin:0 0 14px;">Descrição do conteúdo ao lado da imagem. Ideal para apresentar produtos ou funcionalidades.</p>
          <a href="#" style="color:#2563eb;font-family:Arial,sans-serif;font-size:13px;font-weight:600;text-decoration:none;">Saiba mais →</a>
        </td>
      </tr></table>`,
      attributes: { class: "fa fa-th-large" },
    });

    bm.add("texto-imagem", {
      label: "Texto + Imagem",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr>
        <td width="55%" style="vertical-align:top;padding:8px 16px 8px 8px;">
          <h3 style="color:#1e293b;font-family:Arial,sans-serif;font-size:18px;font-weight:600;margin:0 0 8px;">Título da seção</h3>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.6;margin:0 0 14px;">Descrição do conteúdo ao lado da imagem. Ideal para apresentar produtos ou funcionalidades.</p>
          <a href="#" style="color:#2563eb;font-family:Arial,sans-serif;font-size:13px;font-weight:600;text-decoration:none;">Saiba mais →</a>
        </td>
        <td width="45%" style="vertical-align:top;padding:8px;">
          <img src="https://placehold.co/260x200/e2e8f0/94a3b8?text=Imagem" alt="Imagem" style="width:100%;height:auto;border-radius:8px;display:block;" />
        </td>
      </tr></table>`,
      attributes: { class: "fa fa-th-large" },
    });

    bm.add("banner-hero", {
      label: "Banner Hero",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="background:#1e293b;padding:48px 40px;text-align:center;">
        <h1 style="color:#ffffff;font-family:Arial,sans-serif;font-size:32px;font-weight:800;margin:0 0 10px;">Título Principal</h1>
        <p style="color:#94a3b8;font-family:Arial,sans-serif;font-size:15px;line-height:1.6;margin:0 0 24px;">Subtítulo descritivo que complementa o título principal do e-mail.</p>
        <a href="#" style="display:inline-block;background:#2563eb;color:#fff;font-family:Arial,sans-serif;font-size:15px;font-weight:700;padding:14px 44px;border-radius:8px;text-decoration:none;">Comece agora</a>
      </td></tr></table>`,
      attributes: { class: "fa fa-window-maximize" },
    });

    bm.add("banner-gradiente", {
      label: "Banner Gradiente",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="background:linear-gradient(135deg,#2563eb,#7c3aed);padding:48px 40px;text-align:center;">
        <h1 style="color:#ffffff;font-family:Arial,sans-serif;font-size:30px;font-weight:800;margin:0 0 10px;">Sua Mensagem Aqui</h1>
        <p style="color:#c7d2fe;font-family:Arial,sans-serif;font-size:14px;margin:0 0 24px;">Uma frase de impacto para conquistar o leitor.</p>
        <a href="#" style="display:inline-block;background:#ffffff;color:#2563eb;font-family:Arial,sans-serif;font-size:14px;font-weight:700;padding:12px 36px;border-radius:8px;text-decoration:none;">Descubra mais</a>
      </td></tr></table>`,
      attributes: { class: "fa fa-paint-brush" },
    });

    bm.add("lista-checklist", {
      label: "Lista Check",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0">
        <tr><td style="padding:10px 12px;font-family:Arial,sans-serif;font-size:14px;color:#1e293b;"><span style="color:#059669;font-weight:700;margin-right:8px;">&#10003;</span>Primeiro item da lista de benefícios</td></tr>
        <tr><td style="padding:10px 12px;font-family:Arial,sans-serif;font-size:14px;color:#1e293b;border-top:1px solid #f1f5f9;"><span style="color:#059669;font-weight:700;margin-right:8px;">&#10003;</span>Segundo item da lista de benefícios</td></tr>
        <tr><td style="padding:10px 12px;font-family:Arial,sans-serif;font-size:14px;color:#1e293b;border-top:1px solid #f1f5f9;"><span style="color:#059669;font-weight:700;margin-right:8px;">&#10003;</span>Terceiro item da lista de benefícios</td></tr>
        <tr><td style="padding:10px 12px;font-family:Arial,sans-serif;font-size:14px;color:#1e293b;border-top:1px solid #f1f5f9;"><span style="color:#059669;font-weight:700;margin-right:8px;">&#10003;</span>Quarto item da lista de benefícios</td></tr>
      </table>`,
      attributes: { class: "fa fa-check-square-o" },
    });

    bm.add("3-colunas", {
      label: "3 Cards",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr>
        <td width="32%" style="vertical-align:top;padding:6px;">
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:20px;text-align:center;">
            <div style="width:40px;height:40px;background:#dbeafe;border-radius:10px;margin:0 auto 10px;line-height:40px;font-size:18px;">&#9733;</div>
            <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 6px;">Card 1</h4>
            <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;line-height:1.5;margin:0;">Descrição breve do primeiro card.</p>
          </div>
        </td>
        <td width="2%"></td>
        <td width="32%" style="vertical-align:top;padding:6px;">
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:20px;text-align:center;">
            <div style="width:40px;height:40px;background:#d1fae5;border-radius:10px;margin:0 auto 10px;line-height:40px;font-size:18px;">&#9830;</div>
            <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 6px;">Card 2</h4>
            <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;line-height:1.5;margin:0;">Descrição breve do segundo card.</p>
          </div>
        </td>
        <td width="2%"></td>
        <td width="32%" style="vertical-align:top;padding:6px;">
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:20px;text-align:center;">
            <div style="width:40px;height:40px;background:#fef3c7;border-radius:10px;margin:0 auto 10px;line-height:40px;font-size:18px;">&#9829;</div>
            <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 6px;">Card 3</h4>
            <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;line-height:1.5;margin:0;">Descrição breve do terceiro card.</p>
          </div>
        </td>
      </tr></table>`,
      attributes: { class: "fa fa-th" },
    });

    bm.add("preco-plano", {
      label: "Card de Preço",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:8px;">
        <div style="border:2px solid #2563eb;border-radius:14px;overflow:hidden;text-align:center;">
          <div style="background:#2563eb;padding:16px;">
            <h3 style="color:#ffffff;font-family:Arial,sans-serif;font-size:18px;font-weight:700;margin:0;">Plano Pro</h3>
          </div>
          <div style="padding:24px 20px;">
            <p style="color:#2563eb;font-family:Arial,sans-serif;font-size:36px;font-weight:800;margin:0;">R$ 99<span style="font-size:16px;color:#64748b;font-weight:500;">/mês</span></p>
            <table width="100%" cellpadding="0" cellspacing="0" style="margin:16px 0;">
              <tr><td style="padding:8px 0;color:#475569;font-family:Arial,sans-serif;font-size:13px;border-top:1px solid #f1f5f9;">&#10003; Funcionalidade 1</td></tr>
              <tr><td style="padding:8px 0;color:#475569;font-family:Arial,sans-serif;font-size:13px;border-top:1px solid #f1f5f9;">&#10003; Funcionalidade 2</td></tr>
              <tr><td style="padding:8px 0;color:#475569;font-family:Arial,sans-serif;font-size:13px;border-top:1px solid #f1f5f9;">&#10003; Funcionalidade 3</td></tr>
            </table>
            <a href="#" style="display:inline-block;background:#2563eb;color:#fff;font-family:Arial,sans-serif;font-size:14px;font-weight:700;padding:12px 36px;border-radius:8px;text-decoration:none;">Assinar agora</a>
          </div>
        </div>
      </td></tr></table>`,
      attributes: { class: "fa fa-diamond" },
    });

    bm.add("passo-a-passo", {
      label: "Passo a Passo",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0">
        <tr><td style="padding:12px 0;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td style="width:44px;vertical-align:top;"><div style="width:36px;height:36px;background:#2563eb;border-radius:50%;text-align:center;line-height:36px;color:#fff;font-family:Arial,sans-serif;font-weight:800;font-size:15px;">1</div></td>
            <td style="padding-left:12px;vertical-align:top;">
              <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:15px;font-weight:600;margin:0 0 4px;">Primeiro passo</h4>
              <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;margin:0;">Descrição detalhada do que o usuário precisa fazer nesta etapa.</p>
            </td>
          </tr></table>
        </td></tr>
        <tr><td style="padding:12px 0;border-top:1px solid #f1f5f9;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td style="width:44px;vertical-align:top;"><div style="width:36px;height:36px;background:#2563eb;border-radius:50%;text-align:center;line-height:36px;color:#fff;font-family:Arial,sans-serif;font-weight:800;font-size:15px;">2</div></td>
            <td style="padding-left:12px;vertical-align:top;">
              <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:15px;font-weight:600;margin:0 0 4px;">Segundo passo</h4>
              <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;margin:0;">Descrição detalhada do que o usuário precisa fazer nesta etapa.</p>
            </td>
          </tr></table>
        </td></tr>
        <tr><td style="padding:12px 0;border-top:1px solid #f1f5f9;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td style="width:44px;vertical-align:top;"><div style="width:36px;height:36px;background:#2563eb;border-radius:50%;text-align:center;line-height:36px;color:#fff;font-family:Arial,sans-serif;font-weight:800;font-size:15px;">3</div></td>
            <td style="padding-left:12px;vertical-align:top;">
              <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:15px;font-weight:600;margin:0 0 4px;">Terceiro passo</h4>
              <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;margin:0;">Descrição detalhada do que o usuário precisa fazer nesta etapa.</p>
            </td>
          </tr></table>
        </td></tr>
      </table>`,
      attributes: { class: "fa fa-list-ol" },
    });

    bm.add("video-thumb", {
      label: "Vídeo",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:8px;">
        <a href="#" style="display:block;position:relative;text-decoration:none;">
          <div style="background:#0f172a;border-radius:12px;padding:60px 40px;text-align:center;">
            <div style="width:64px;height:64px;background:rgba(255,255,255,0.2);border-radius:50%;margin:0 auto;line-height:64px;">
              <span style="color:#ffffff;font-size:28px;margin-left:4px;">&#9654;</span>
            </div>
            <p style="color:#94a3b8;font-family:Arial,sans-serif;font-size:13px;margin:12px 0 0;">Clique para assistir o vídeo</p>
          </div>
        </a>
      </td></tr></table>`,
      attributes: { class: "fa fa-play-circle" },
    });

    bm.add("faq-bloco", {
      label: "FAQ",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0">
        <tr><td style="padding:14px 16px;border-bottom:1px solid #e2e8f0;">
          <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 6px;">Pergunta frequente número 1?</h4>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.6;margin:0;">Resposta detalhada para a pergunta do cliente. Seja claro e objetivo.</p>
        </td></tr>
        <tr><td style="padding:14px 16px;border-bottom:1px solid #e2e8f0;">
          <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 6px;">Pergunta frequente número 2?</h4>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.6;margin:0;">Resposta detalhada para a pergunta do cliente. Seja claro e objetivo.</p>
        </td></tr>
        <tr><td style="padding:14px 16px;">
          <h4 style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 6px;">Pergunta frequente número 3?</h4>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.6;margin:0;">Resposta detalhada para a pergunta do cliente. Seja claro e objetivo.</p>
        </td></tr>
      </table>`,
      attributes: { class: "fa fa-question-circle" },
    });

    bm.add("assinatura", {
      label: "Assinatura",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:20px 0;">
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="vertical-align:top;padding-right:16px;">
            <div style="width:56px;height:56px;background:#2563eb;border-radius:50%;text-align:center;line-height:56px;color:#fff;font-family:Arial,sans-serif;font-size:22px;font-weight:700;">J</div>
          </td>
          <td style="vertical-align:top;">
            <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:15px;font-weight:700;margin:0;">João Silva</p>
            <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;margin:2px 0 0;">Diretor Comercial</p>
            <p style="color:#94a3b8;font-family:Arial,sans-serif;font-size:12px;margin:2px 0 0;">joao@empresa.com.br | (11) 99999-9999</p>
          </td>
        </tr></table>
      </td></tr></table>`,
      attributes: { class: "fa fa-user-circle" },
    });

    bm.add("selo-garantia", {
      label: "Selo Garantia",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:20px 0;">
        <div style="display:inline-block;border:3px solid #059669;border-radius:50%;width:100px;height:100px;text-align:center;padding-top:20px;">
          <p style="color:#059669;font-family:Arial,sans-serif;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin:0;">Garantia</p>
          <p style="color:#059669;font-family:Arial,sans-serif;font-size:24px;font-weight:800;margin:0;">30</p>
          <p style="color:#059669;font-family:Arial,sans-serif;font-size:10px;font-weight:600;margin:0;">DIAS</p>
        </div>
        <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;margin:10px 0 0;">Satisfação garantida ou seu dinheiro de volta</p>
      </td></tr></table>`,
      attributes: { class: "fa fa-shield" },
    });

    bm.add("download-app", {
      label: "Baixe o App",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="text-align:center;padding:24px 0;">
        <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:16px;font-weight:700;margin:0 0 16px;">Baixe nosso aplicativo</p>
        <a href="#" style="display:inline-block;background:#0f172a;color:#fff;font-family:Arial,sans-serif;font-size:12px;padding:10px 20px;border-radius:8px;text-decoration:none;margin:0 4px;">
          <span style="font-size:16px;vertical-align:middle;">&#63743;</span> App Store
        </a>
        <a href="#" style="display:inline-block;background:#0f172a;color:#fff;font-family:Arial,sans-serif;font-size:12px;padding:10px 20px;border-radius:8px;text-decoration:none;margin:0 4px;">
          <span style="font-size:16px;vertical-align:middle;">&#9654;</span> Google Play
        </a>
      </td></tr></table>`,
      attributes: { class: "fa fa-mobile" },
    });

    bm.add("separador-texto", {
      label: "Separador Texto",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr>
        <td style="padding:20px 0;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td style="width:40%;border-bottom:1px solid #e2e8f0;"></td>
            <td style="text-align:center;padding:0 12px;white-space:nowrap;">
              <span style="color:#94a3b8;font-family:Arial,sans-serif;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;">Seção</span>
            </td>
            <td style="width:40%;border-bottom:1px solid #e2e8f0;"></td>
          </tr></table>
        </td>
      </tr></table>`,
      attributes: { class: "fa fa-text-width" },
    });

    bm.add("countdown-urgencia", {
      label: "Urgência",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:20px;text-align:center;">
        <p style="color:#dc2626;font-family:Arial,sans-serif;font-size:12px;text-transform:uppercase;letter-spacing:2px;font-weight:700;margin:0 0 8px;">Oferta expira em</p>
        <table cellpadding="0" cellspacing="0" style="margin:0 auto;">
          <tr>
            <td style="text-align:center;padding:0 8px;">
              <div style="background:#dc2626;color:#fff;font-family:Arial,sans-serif;font-size:24px;font-weight:800;width:48px;height:48px;line-height:48px;border-radius:8px;">02</div>
              <p style="color:#dc2626;font-family:Arial,sans-serif;font-size:10px;margin:4px 0 0;">DIAS</p>
            </td>
            <td style="color:#dc2626;font-family:Arial,sans-serif;font-size:24px;font-weight:800;">:</td>
            <td style="text-align:center;padding:0 8px;">
              <div style="background:#dc2626;color:#fff;font-family:Arial,sans-serif;font-size:24px;font-weight:800;width:48px;height:48px;line-height:48px;border-radius:8px;">14</div>
              <p style="color:#dc2626;font-family:Arial,sans-serif;font-size:10px;margin:4px 0 0;">HORAS</p>
            </td>
            <td style="color:#dc2626;font-family:Arial,sans-serif;font-size:24px;font-weight:800;">:</td>
            <td style="text-align:center;padding:0 8px;">
              <div style="background:#dc2626;color:#fff;font-family:Arial,sans-serif;font-size:24px;font-weight:800;width:48px;height:48px;line-height:48px;border-radius:8px;">37</div>
              <p style="color:#dc2626;font-family:Arial,sans-serif;font-size:10px;margin:4px 0 0;">MIN</p>
            </td>
          </tr>
        </table>
      </td></tr></table>`,
      attributes: { class: "fa fa-clock-o" },
    });

    bm.add("equipe-membro", {
      label: "Membro Equipe",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr>
        <td width="33%" style="text-align:center;padding:12px;">
          <div style="width:72px;height:72px;background:#dbeafe;border-radius:50%;margin:0 auto 8px;line-height:72px;color:#2563eb;font-family:Arial,sans-serif;font-size:28px;font-weight:700;">M</div>
          <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:13px;font-weight:600;margin:0;">Maria Silva</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:11px;margin:2px 0 0;">CEO</p>
        </td>
        <td width="33%" style="text-align:center;padding:12px;">
          <div style="width:72px;height:72px;background:#d1fae5;border-radius:50%;margin:0 auto 8px;line-height:72px;color:#059669;font-family:Arial,sans-serif;font-size:28px;font-weight:700;">J</div>
          <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:13px;font-weight:600;margin:0;">João Santos</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:11px;margin:2px 0 0;">CTO</p>
        </td>
        <td width="33%" style="text-align:center;padding:12px;">
          <div style="width:72px;height:72px;background:#fef3c7;border-radius:50%;margin:0 auto 8px;line-height:72px;color:#d97706;font-family:Arial,sans-serif;font-size:28px;font-weight:700;">A</div>
          <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:13px;font-weight:600;margin:0;">Ana Costa</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:11px;margin:2px 0 0;">CMO</p>
        </td>
      </tr></table>`,
      attributes: { class: "fa fa-users" },
    });

    bm.add("endereco-local", {
      label: "Endereço",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:8px;">
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:20px;text-align:center;">
          <div style="width:40px;height:40px;background:#fee2e2;border-radius:10px;margin:0 auto 10px;line-height:40px;font-size:18px;">&#128205;</div>
          <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 4px;">Nosso endereço</p>
          <p style="color:#64748b;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;margin:0;">Av. Paulista, 1000 - Sala 101<br/>São Paulo, SP - 01310-100</p>
          <a href="#" style="display:inline-block;margin-top:12px;color:#2563eb;font-family:Arial,sans-serif;font-size:13px;font-weight:600;text-decoration:none;">Ver no mapa →</a>
        </div>
      </td></tr></table>`,
      attributes: { class: "fa fa-map-marker" },
    });

    bm.add("progresso-barra", {
      label: "Barra Progresso",
      category: "Prontos",
      content: `<table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:16px 0;">
        <p style="color:#1e293b;font-family:Arial,sans-serif;font-size:14px;font-weight:600;margin:0 0 8px;">75% concluído</p>
        <div style="width:100%;height:12px;background:#e2e8f0;border-radius:6px;overflow:hidden;">
          <div style="width:75%;height:100%;background:linear-gradient(90deg,#2563eb,#7c3aed);border-radius:6px;"></div>
        </div>
        <p style="color:#64748b;font-family:Arial,sans-serif;font-size:12px;margin:6px 0 0;">Falta pouco para completar sua meta!</p>
      </td></tr></table>`,
      attributes: { class: "fa fa-tasks" },
    });

    // Force BlockManager re-render with PRONTOS first, PARA CRIAR second
    const allBlocks = bm.getAll().models;
    const prontos = allBlocks.filter(b => {
      const cat = b.get("category");
      return cat === "Prontos" || cat?.id === "Prontos" || cat?.label === "Prontos";
    });
    const paraCriar = allBlocks.filter(b => {
      const cat = b.get("category");
      return !(cat === "Prontos" || cat?.id === "Prontos" || cat?.label === "Prontos");
    });
    bm.render([...prontos, ...paraCriar]);

    // Load existing data
    try {
      let gjsData = template?.gjs_data;
      // Fix double-encoded JSON strings from previous saves
      if (typeof gjsData === "string") {
        try { gjsData = JSON.parse(gjsData); } catch { gjsData = null; }
      }
      if (gjsData && typeof gjsData === "object" && Object.keys(gjsData).length > 0) {
        editor.loadProjectData(gjsData);
      } else if (template?.html) {
        editor.setComponents(template.html);
      }
    } catch (err) {
      console.warn("Failed to load GrapeJS project data, loading HTML fallback:", err);
      try { if (template?.html) editor.setComponents(template.html); } catch (e2) { /* ignore */ }
    }

    gjsRef.current = editor;

    return () => {
      if (gjsRef.current) {
        gjsRef.current.destroy();
        gjsRef.current = null;
      }
    };
  }, []);

  const handleSave = useCallback(async (status) => {
    if (!name.trim()) {
      toast.error("Nome do modelo é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      const editor = gjsRef.current;
      const html = editor ? editor.runCommand("gjs-get-inlined-html") : "";
      const gjs_data = editor ? editor.getProjectData() : {};
      const ok = await saveToServer({ name, description, html, gjs_data, status });
      if (ok) toast.success("Modelo salvo!");
      else toast.error("Erro ao salvar.");
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }, [name, description, saveToServer]);

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Informe o e-mail de destino.");
      return;
    }
    const tid = templateIdRef.current;
    if (!tid) {
      toast.error("Salve o modelo antes de enviar o teste.");
      return;
    }
    setSendingTest(true);
    try {
      const res = await fetch(`${API}/api/email-templates/${tid}/send-test`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email: testEmail }),
      });
      if (res.ok) toast.success("E-mail de teste enviado!");
      else toast.error("Erro ao enviar teste.");
    } catch {
      toast.error("Erro de conexão.");
    } finally {
      setSendingTest(false);
    }
  };

  const handleDeviceChange = (device) => {
    const editor = gjsRef.current;
    if (editor) editor.setDevice(device);
  };

  const [showTestModal, setShowTestModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const editor = gjsRef.current;
      if (!editor || saving) return;
      const html = editor.runCommand("gjs-get-inlined-html") || "";
      const gjsData = editor.getProjectData();
      if (!html) return;
      try {
        const ok = await saveToServer({ name, description, html, gjs_data: gjsData, status: "rascunho" });
        if (ok) { setShowSavedIndicator(true); setTimeout(() => setShowSavedIndicator(false), 3000); }
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [name, description, saving, saveToServer]);

  const panelTabs = [
    { id: "blocks", label: "Blocos", icon: LayoutGrid },
    { id: "gradient", label: "Fundo", icon: Palette },
    { id: "styles", label: "Estilos", icon: SlidersHorizontal },
    { id: "variables", label: "Variáveis", icon: Braces },
    { id: "layers", label: "Camadas", icon: Layers },
  ];

  // Gradient panel state
  const [gradColor1, setGradColor1] = useState("#2563eb");
  const [gradColor2, setGradColor2] = useState("#7c3aed");
  const [gradDirection, setGradDirection] = useState("135deg");
  const [bgType, setBgType] = useState("gradient"); // gradient | solid
  const [solidColor, setSolidColor] = useState("#2563eb");

  // Apply background in real-time when settings change
  const applyBackgroundRT = useCallback(() => {
    const editor = gjsRef.current;
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) return;
    if (bgType === "solid") {
      selected.addStyle({ "background": solidColor, "background-image": "none" });
    } else {
      selected.addStyle({ "background": `linear-gradient(${gradDirection}, ${gradColor1}, ${gradColor2})` });
    }
  }, [bgType, solidColor, gradColor1, gradColor2, gradDirection]);

  // Trigger real-time application on any bg setting change
  useEffect(() => {
    applyBackgroundRT();
  }, [applyBackgroundRT]);

  const removeBg = useCallback(() => {
    const editor = gjsRef.current;
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) { toast.error("Selecione um elemento primeiro."); return; }
    selected.addStyle({ "background": "transparent", "background-image": "none", "background-color": "transparent" });
    toast.success("Fundo removido!");
  }, []);

  const directions = [
    { value: "to right", label: "→", title: "Esquerda para Direita" },
    { value: "to left", label: "←", title: "Direita para Esquerda" },
    { value: "to bottom", label: "↓", title: "Cima para Baixo" },
    { value: "to top", label: "↑", title: "Baixo para Cima" },
    { value: "135deg", label: "↘", title: "Diagonal ↘" },
    { value: "45deg", label: "↗", title: "Diagonal ↗" },
    { value: "225deg", label: "↙", title: "Diagonal ↙" },
    { value: "315deg", label: "↖", title: "Diagonal ↖" },
  ];

  const presetGradients = [
    { c1: "#2563eb", c2: "#7c3aed", name: "Azul → Roxo" },
    { c1: "#059669", c2: "#0284c7", name: "Verde → Azul" },
    { c1: "#f59e0b", c2: "#ef4444", name: "Amarelo → Vermelho" },
    { c1: "#ec4899", c2: "#8b5cf6", name: "Rosa → Violeta" },
    { c1: "#0f172a", c2: "#334155", name: "Escuro" },
    { c1: "#dc2626", c2: "#ea580c", name: "Vermelho → Laranja" },
    { c1: "#0ea5e9", c2: "#6366f1", name: "Céu → Índigo" },
    { c1: "#10b981", c2: "#14b8a6", name: "Esmeralda → Teal" },
  ];

  const presetSolids = [
    "#2563eb", "#7c3aed", "#059669", "#dc2626", "#f59e0b", "#ec4899",
    "#0284c7", "#0f172a", "#1e293b", "#334155", "#475569", "#ffffff",
    "#f8fafc", "#f1f5f9", "#e2e8f0", "#fef3c7", "#fee2e2", "#d1fae5",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header — FORA do card branco */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
          <Mail className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">Editando modelo</h1>
          <p className="text-slate-500 mt-0.5">Personalize em detalhes o e-mail.</p>
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 h-8" data-testid="editor-back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Button>
        <div className="h-5 w-px bg-slate-200" />
        <Tooltip><TooltipTrigger asChild>
          <Button variant="outline" size="icon" onClick={() => setShowEditModal(true)} className="h-8 w-8" data-testid="edit-name-btn">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger><TooltipContent>Editar nome e descrição</TooltipContent></Tooltip>
        <Tooltip><TooltipTrigger asChild>
          <Button variant="outline" size="icon" onClick={() => setShowTestModal(true)} className="h-8 w-8" data-testid="send-test-btn">
            <Send className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger><TooltipContent>Enviar e-mail de teste</TooltipContent></Tooltip>
        <Tooltip><TooltipTrigger asChild>
          <Button variant="outline" size="icon" onClick={() => handleSave("rascunho")} disabled={saving} className="h-8 w-8" data-testid="save-draft-btn">
            <Save className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger><TooltipContent>Salvar rascunho</TooltipContent></Tooltip>
        <Button size="sm" onClick={() => handleSave("publicado")} disabled={saving} className="gap-1 h-8 text-xs" data-testid="publish-template-btn">
          <Eye className="w-3 h-3" /> Publicar
        </Button>
      </div>
      {/* Toolbar — undo/redo + device */}
      <div className="flex items-center gap-2 px-4 py-1.5 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-0.5">
          <button onClick={handleUndo} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Desfazer">
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={handleRedo} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Refazer">
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
        <div className="h-5 w-px bg-slate-200" />
        <div className="flex items-center gap-0.5">
          {[{name:"Desktop",Icon:Monitor},{name:"Tablet",Icon:Tablet},{name:"Mobile",Icon:Smartphone}].map(d => (
            <button key={d.name} onClick={() => handleDeviceChange(d.name)}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title={d.name}>
              <d.Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
        {showSavedIndicator && (
          <div className="flex items-center gap-1 text-green-600 text-xs font-medium animate-in fade-in ml-2">
            <Check className="w-3.5 h-3.5" /> Salvo!
          </div>
        )}
      </div>

      {/* Editor + Side Panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* GrapeJS Canvas */}
        <div className="flex-1 overflow-hidden" data-testid="grapejs-editor">
          <div ref={editorRef} className="h-full" />
        </div>

        {/* Right Panel */}
        <div className="w-[300px] bg-white border-l border-slate-200 flex flex-col flex-shrink-0">
          {/* Panel Tabs */}
          <div className="flex border-b border-slate-200">
            {panelTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <Tooltip key={tab.id}>
                  <TooltipTrigger asChild>
                    <button onClick={() => setActivePanel(tab.id)}
                      data-testid={`panel-tab-${tab.id}`}
                      className={`flex-1 flex items-center justify-center py-2.5 transition-colors ${
                        activePanel === tab.id
                          ? "border-b-2 bg-opacity-10"
                          : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                      }`}
                      style={activePanel === tab.id ? { color: tc500, borderBottomColor: tc500, backgroundColor: tc50 } : {}}>
                      <Icon className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">{tab.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          {/* Panel Contents */}
          <div className="flex-1 overflow-y-auto">
            <div id="gjs-blocks-panel" className={activePanel === "blocks" ? "" : "hidden"} />

            {/* Gradient / Background Panel */}
            <div className={activePanel === "gradient" ? "p-3 space-y-4" : "hidden"}>
              {/* Type Toggle */}
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Tipo de fundo</p>
                <div className="flex gap-1">
                  <button onClick={() => setBgType("gradient")}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${bgType === "gradient" ? "text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                    style={bgType === "gradient" ? { backgroundColor: tc500 } : {}}>
                    Degradê
                  </button>
                  <button onClick={() => setBgType("solid")}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${bgType === "solid" ? "text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                    style={bgType === "solid" ? { backgroundColor: tc500 } : {}}>
                    Cor sólida
                  </button>
                </div>
              </div>

              {bgType === "gradient" ? (
                <>
                  {/* Color pickers */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Cores</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="text-[10px] text-slate-400 mb-1 block">Cor 1</label>
                        <div className="flex items-center gap-2 bg-slate-50 rounded-md p-1.5 border border-slate-200">
                          <input type="color" value={gradColor1} onChange={e => setGradColor1(e.target.value)}
                            className="w-8 h-8 rounded cursor-pointer border-0" style={{ padding: 0 }} />
                          <span className="text-[11px] text-slate-500 font-mono">{gradColor1}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] text-slate-400 mb-1 block">Cor 2</label>
                        <div className="flex items-center gap-2 bg-slate-50 rounded-md p-1.5 border border-slate-200">
                          <input type="color" value={gradColor2} onChange={e => setGradColor2(e.target.value)}
                            className="w-8 h-8 rounded cursor-pointer border-0" style={{ padding: 0 }} />
                          <span className="text-[11px] text-slate-500 font-mono">{gradColor2}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Direction */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Direção</p>
                    <div className="grid grid-cols-4 gap-1">
                      {directions.map(d => (
                        <button key={d.value} onClick={() => setGradDirection(d.value)} title={d.title}
                          className={`h-9 rounded-md text-base font-bold transition-colors ${gradDirection === d.value ? "text-white border" : "bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100"}`}
                          style={gradDirection === d.value ? { backgroundColor: tc500, borderColor: tc500 } : {}}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preset gradients */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Prontos</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {presetGradients.map((pg, i) => (
                        <button key={i} onClick={() => { setGradColor1(pg.c1); setGradColor2(pg.c2); }} title={pg.name}
                          className="h-8 rounded-md border border-slate-200 hover:scale-105 transition-transform cursor-pointer"
                          style={{ background: `linear-gradient(135deg, ${pg.c1}, ${pg.c2})` }} />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Solid color picker */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Cor</p>
                    <div className="flex items-center gap-2 bg-slate-50 rounded-md p-2 border border-slate-200">
                      <input type="color" value={solidColor} onChange={e => setSolidColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border-0" style={{ padding: 0 }} />
                      <span className="text-[11px] text-slate-500 font-mono">{solidColor}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Cores rápidas</p>
                    <div className="grid grid-cols-6 gap-1.5">
                      {presetSolids.map((c, i) => (
                        <button key={i} onClick={() => setSolidColor(c)}
                          className="w-full h-7 rounded-md border border-slate-200 hover:scale-110 transition-transform cursor-pointer"
                          style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Remove */}
              <div className="pt-1">
                <Button variant="outline" size="sm" onClick={removeBg} className="w-full h-8 text-xs">
                  Remover
                </Button>
              </div>
            </div>

            {/* Custom React Style Panels (replaces native GrapeJS StyleManager) */}
            <div className={activePanel === "styles" ? "" : "hidden"}>
              <StylePanels editorRef={gjsRef} tc500={tc500} />
            </div>

            {/* Variables Panel */}
            <div className={activePanel === "variables" ? "p-3" : "hidden"}>
              <VariablesPanel editorRef={gjsRef} tc500={tc500} tc50={tc50} />
            </div>

            <div id="gjs-styles-native-hidden" className="hidden" />
            <div id="gjs-traits-panel" className="hidden" />
            <div id="gjs-layers-panel" className={activePanel === "layers" ? "" : "hidden"} />
          </div>
        </div>
      </div>

      {/* Modal Enviar Teste */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" /> Enviar e-mail de teste
            </DialogTitle>
            <DialogDescription>
              Informe o endereço de e-mail que receberá uma prévia deste modelo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              data-testid="test-email-input"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              type="email"
              className="h-10"
              onKeyDown={(e) => { if (e.key === "Enter") { handleSendTest(); } }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestModal(false)}>Cancelar</Button>
            <Button onClick={() => { handleSendTest(); setShowTestModal(false); }} disabled={sendingTest} className="gap-1.5" data-testid="confirm-send-test-btn">
              <Send className="w-4 h-4" /> Enviar teste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Editar Nome/Descrição */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" style={{ color: tc500 }} /> Editar modelo
            </DialogTitle>
            <DialogDescription>
              Altere o nome e a descrição deste modelo de e-mail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Nome</label>
              <Input
                data-testid="edit-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Descrição</label>
              <textarea
                data-testid="edit-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button onClick={() => setShowEditModal(false)} className="gap-1.5" data-testid="confirm-edit-btn">
              <Save className="w-4 h-4" /> Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function EmailModelosPage() {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#F43F5E";
  const tc50 = appearance.colors?.["50"] || "#FFF1F2";
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, object = editing
  const [showGallery, setShowGallery] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/email-templates`, { headers: getHeaders() });
      if (res.ok) setTemplates(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleCreate = () => {
    setShowGallery(true);
  };

  const handleStarterSelect = (starter) => {
    setShowGallery(false);
    setEditing({
      name: starter.id === "blank" ? "" : starter.name.replace(/&[^;]+;/g, match => {
        const el = document.createElement("span");
        el.innerHTML = match;
        return el.textContent;
      }),
      description: "",
      html: starter.html,
      gjs_data: {},
      status: "rascunho",
    });
  };

  const handleDuplicate = async (id) => {
    await fetch(`${API}/api/email-templates/${id}/duplicate`, {
      method: "POST",
      headers: getHeaders(),
    });
    fetchTemplates();
    toast.success("Modelo duplicado!");
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/api/email-templates/${id}`, {
      method: "DELETE",
      headers: getHeaders(),
    });
    fetchTemplates();
    toast.success("Modelo excluído!");
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <img src="/brand/icon-1.png" alt="Loading" className="w-10 h-10 animate-pulse rounded-xl" />
    </div>
  );

  if (showGallery) {
    return <StarterGallery onSelect={handleStarterSelect} onBack={() => setShowGallery(false)} tc500={tc500} tc50={tc50} />;
  }

  if (editing !== null) {
    return (
      <TemplateEditor
        key={editing?.template_id || "new"}
        template={editing}
        onBack={() => { setEditing(null); fetchTemplates(); }}
        tc500={tc500}
        tc50={tc50}
      />
    );
  }

  return (
    <div className="space-y-4">
      <TemplateList
        templates={templates}
        onEdit={setEditing}
        onCreate={handleCreate}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        tc500={tc500}
        tc50={tc50}
      />
    </div>
  );
}
